import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bike,
  Check,
  ChefHat,
  ClipboardList,
  PackageCheck,
  RefreshCw,
  Search,
  Store,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  placed: "Order received",
  confirmed: "Confirmed",
  baking: "In the oven",
  out_for_delivery: "Out for delivery",
  ready: "Ready for pickup",
  completed: "Completed",
  cancelled: "Cancelled",
};
const ICONS = {
  placed: ClipboardList,
  confirmed: Check,
  baking: ChefHat,
  out_for_delivery: Bike,
  ready: Store,
  completed: PackageCheck,
};
const stepsFor = (ful) =>
  ful === "delivery"
    ? ["placed", "confirmed", "baking", "out_for_delivery", "completed"]
    : ["placed", "confirmed", "baking", "ready", "completed"];

export default function TrackOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lookup, setLookup] = useState("");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState(id ? "loading" : "idle");

  const load = useCallback(async (oid, silent) => {
    if (!silent) setState("loading");
    try {
      const r = await fetch(`${API}/orders/${oid}/track`);
      if (r.status === 404) {
        setState("notfound");
        return;
      }
      if (!r.ok) throw new Error("bad response");
      setOrder(await r.json());
      setState("ok");
    } catch {
      if (!silent) setState("error");
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setState("idle");
      setOrder(null);
      return;
    }
    load(id, false);
    const t = setInterval(() => load(id, true), 15000);
    return () => clearInterval(t);
  }, [id, load]);

  return (
    <section data-testid="track-page" className="pt-32 pb-24 lg:pt-40">
      <div className="max-w-2xl mx-auto px-6">
        <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32] mb-4">
          Order tracking
        </p>
        <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl">
          Where's my bake?
        </h1>

        {state === "idle" && (
          <form
            className="mt-10"
            onSubmit={(e) => {
              e.preventDefault();
              const v = lookup.trim();
              if (v) navigate(`/track/${v}`);
            }}
          >
            <label className="block">
              <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">
                Order number
              </span>
              <div className="flex gap-3 mt-2">
                <input
                  className="field flex-1"
                  data-testid="track-lookup-input"
                  placeholder="Paste the order number from your confirmation"
                  value={lookup}
                  onChange={(e) => setLookup(e.target.value)}
                />
                <button
                  type="submit"
                  data-testid="track-lookup-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1C130E] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors"
                >
                  <Search size={15} /> Track
                </button>
              </div>
            </label>
          </form>
        )}

        {state === "loading" && (
          <p className="mt-10 text-[#1C130E]/60" data-testid="track-loading">
            Looking up your order…
          </p>
        )}

        {state === "notfound" && (
          <div className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-6 py-8" data-testid="track-not-found">
            <p className="text-[#1C130E]/75">
              We couldn't find that order number. Double-check it from your confirmation, or call
              us on +91 95967 66336 and we'll find it for you.
            </p>
            <Link to="/track" data-testid="track-back-link" className="mt-4 inline-block text-sm font-semibold text-[#C85A32] hover:underline">
              Try another number
            </Link>
          </div>
        )}

        {state === "error" && (
          <div className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-6 py-8">
            <p className="text-[#1C130E]/75">Something went wrong — please try again.</p>
            <button
              onClick={() => load(id, false)}
              className="mt-4 inline-block text-sm font-semibold text-[#C85A32] hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {state === "ok" && order && (
          <div className="mt-10" data-testid="track-timeline">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#1C130E]/45" data-testid="track-order-number">
                Order #{order.id.slice(0, 8).toUpperCase()}
              </p>
              <button
                onClick={() => load(order.id, true)}
                data-testid="track-refresh-btn"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#1C130E]/60 hover:text-[#C85A32] transition-colors"
              >
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {order.status === "cancelled" ? (
              <div className="mt-6 rounded-2xl border border-red-300 bg-red-50 px-5 py-4" data-testid="track-cancelled">
                <p className="font-semibold text-red-700">This order was cancelled.</p>
                <p className="text-sm text-red-600/80 mt-1">
                  If this is a surprise, call us on +91 95967 66336.
                </p>
              </div>
            ) : (
              <ol className="mt-8 space-y-0">
                {stepsFor(order.fulfillment).map((key, i, arr) => {
                  const currentIdx = stepsFor(order.fulfillment).indexOf(order.status);
                  const done = i <= currentIdx;
                  const current = i === currentIdx;
                  const Icon = ICONS[key] || Check;
                  return (
                    <li key={key} data-testid={`track-step-${key}`} className="relative flex gap-4 pb-8 last:pb-0">
                      {i < arr.length - 1 && (
                        <span
                          className={`absolute left-[17px] top-9 bottom-0 w-px ${i < currentIdx ? "bg-[#C85A32]" : "bg-[rgba(28,19,14,0.12)]"}`}
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={`relative z-10 grid place-items-center w-9 h-9 rounded-full border shrink-0 transition-colors ${
                          done
                            ? "bg-[#C85A32] border-[#C85A32] text-[#FDFBF7]"
                            : "bg-[#FDFBF7] border-[rgba(28,19,14,0.15)] text-[#1C130E]/35"
                        } ${current ? "ring-4 ring-[#C85A32]/15" : ""}`}
                      >
                        <Icon size={15} />
                      </span>
                      <span className="pt-2">
                        <span className={`block font-display text-lg leading-tight ${done ? "text-[#1C130E]" : "text-[#1C130E]/40"}`}>
                          {LABELS[key]}
                          {current && <span className="sr-only"> (current step)</span>}
                        </span>
                        {current && (
                          <span className="block text-xs text-[#C85A32] mt-0.5 font-medium">Current status</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}

            <div className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-4" data-testid="track-order-meta">
              <div className="flex justify-between text-sm text-[#1C130E]/70" data-testid="track-order-items">
                <span>
                  {order.items.map((i) => `${i.qty} × ${i.name}`).join(", ")}
                </span>
              </div>
              <div className="flex justify-between text-sm text-[#1C130E]/70 mt-1.5">
                <span data-testid="track-order-fulfillment">
                  {order.fulfillment === "delivery"
                    ? `Home delivery${order.delivery_time ? ` · within ${order.delivery_time}` : ""}`
                    : "Store pickup · Havells Galaxy, Bemina"}
                </span>
                <span className="font-mono-accent" data-testid="track-order-total">₹{order.total}</span>
              </div>
              <p className="text-xs text-[#1C130E]/45 mt-2 font-mono-accent uppercase tracking-[0.15em]">
                {order.fulfillment === "delivery" ? "Delivery time confirmed by our team" : `Reserved for ${order.pickup_date}`}
              </p>
            </div>
          </div>
        )}

        <Link
          to="/"
          data-testid="track-home-link"
          className="mt-12 inline-flex items-center justify-center rounded-full bg-[#1C130E] text-[#FDFBF7] px-8 py-4 text-sm font-semibold hover:bg-[#C85A32] transition-colors"
        >
          Back to home
        </Link>
      </div>
    </section>
  );
}
